import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, ArrowLeft, CheckCircle2, Clock, FileText } from 'lucide-react';

export default function WarrantyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-50/50 to-white py-12 px-4 sm:px-6 relative overflow-hidden">
      
      {/* Hiệu ứng ánh sáng nền trang trí mờ ảo phía sau */}
      <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-indigo-500/5 blur-[120px] pointer-events-none rounded-full"></div>

      <div className="max-w-4xl mx-auto space-y-6 relative z-10">
        
        {/* Nút quay lại cao cấp */}
        <div>
          <button
            onClick={() => navigate("/")}
            className="group inline-flex items-center gap-2.5 text-xs font-bold text-slate-600 hover:text-indigo-600 bg-white hover:bg-indigo-50/50 border border-slate-200/80 px-4 py-2.5 rounded-2xl transition-all duration-200 shadow-sm hover:shadow cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" /> 
            <span>Quay lại</span>
          </button>
        </div>

        {/* Khung nội dung chính (Card) */}
        <div className="bg-white/95 backdrop-blur-xl border border-slate-200/80 rounded-3xl p-8 sm:p-12 space-y-8 shadow-2xl shadow-slate-200/50">
          
          {/* Header tiêu đề trang */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center shrink-0 shadow-inner">
                <ShieldCheck className="w-8 h-8" />
              </div>
              <div>
                <span className="text-[11px] font-bold uppercase tracking-widest text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full">
                  Chính sách chính hãng
                </span>
                <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-serif mt-1.5">
                  Chính Sách Bảo Hành
                </h1>
              </div>
            </div>
            <div className="text-slate-400 text-xs flex items-center gap-1.5 self-start sm:self-center">
              <Clock className="w-3.5 h-3.5" /> Cập nhật mới nhất 2026
            </div>
          </div>

          {/* Phần mở đầu dạng khối thông điệp nổi bật */}
          <div className="bg-gradient-to-r from-indigo-50/60 to-slate-50 border border-indigo-100/60 p-5 sm:p-6 rounded-2xl text-slate-700 leading-relaxed text-xs sm:text-sm font-medium">
            Tại <strong className="text-indigo-600">JapanICHIBA</strong>, chúng tôi cam kết phân phối các sản phẩm điện tử, thời trang cao cấp chính hãng 100% từ Nhật Bản. Tất cả đều tuân thủ nghiêm ngặt tiêu chuẩn bảo hành quốc tế nhằm mang lại sự an tâm tuyệt đối cho quý khách.
          </div>

          {/* Nội dung chi tiết */}
          <div className="space-y-8 text-xs sm:text-sm text-slate-600 leading-relaxed">
            
            {/* Mục 1 */}
            <div className="space-y-3">
              <h3 className="font-bold text-slate-900 text-base sm:text-lg flex items-center gap-2.5">
                <span className="w-7 h-7 bg-slate-900 text-white rounded-xl flex items-center justify-center text-xs shadow-sm">1</span>
                Thời Gian Bảo Hành Sản Phẩm
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                <div className="bg-slate-50/80 border border-slate-200/60 p-4 rounded-2xl space-y-1.5">
                  <span className="font-bold text-slate-900 text-xs uppercase tracking-wide text-indigo-600">Thiết bị điện tử & Công nghệ</span>
                  <p className="text-slate-600 text-xs">Bảo hành chính hãng từ 12 đến 24 tháng tùy theo từng dòng sản phẩm mã vạch.</p>
                </div>
                <div className="bg-slate-50/80 border border-slate-200/60 p-4 rounded-2xl space-y-1.5">
                  <span className="font-bold text-slate-900 text-xs uppercase tracking-wide text-indigo-600">Thời trang & Giày dép cao cấp</span>
                  <p className="text-slate-600 text-xs">Hỗ trợ bảo hành đường may, keo dán, phụ kiện đính kèm trong vòng 6 tháng đầu.</p>
                </div>
              </div>
            </div>

            {/* Mục 2 */}
            <div className="space-y-3">
              <h3 className="font-bold text-slate-900 text-base sm:text-lg flex items-center gap-2.5">
                <span className="w-7 h-7 bg-slate-900 text-white rounded-xl flex items-center justify-center text-xs shadow-sm">2</span>
                Điều Kiện Bảo Hành Miễn Phí
              </h3>
              <ul className="space-y-2 pl-2">
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span>Sản phẩm còn trong thời hạn bảo hành tính từ thời điểm mua hàng.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span>Có hóa đơn mua hàng điện tử hoặc phiếu bảo hành hợp lệ từ hệ thống JapanICHIBA.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span>Lỗi kỹ thuật phát sinh được xác định xuất phát từ phía nhà sản xuất.</span>
                </li>
              </ul>
            </div>

            {/* Mục 3 */}
            <div className="space-y-3">
              <h3 className="font-bold text-slate-900 text-base sm:text-lg flex items-center gap-2.5">
                <span className="w-7 h-7 bg-slate-900 text-white rounded-xl flex items-center justify-center text-xs shadow-sm">3</span>
                Trường Hợp Không Đủ Điều Kiện Bảo Hành
              </h3>
              <div className="bg-amber-50/50 border border-amber-200/60 p-4 rounded-2xl text-amber-900 text-xs space-y-1.5">
                <p className="font-bold">Lưu ý:</p>
                <p>Sản phẩm bị hư hỏng do tác động ngoại lực, rơi vỡ, ngập nước, hoặc đã qua sửa chữa ở các đơn vị không thuộc ủy quyền của hãng sẽ không được áp dụng chính sách bảo hành miễn phí.</p>
              </div>
            </div>

          </div>

        </div>
      </div>
    </div>
  );
}